import React, { useState, useCallback, useMemo } from 'react';

import { UnifiedDialogContext, UnreadState, UpdateType } from './UnifiedDialogContext';

const UnifiedDialogContextProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [unread, setUnread] = useState<UnreadState>({
    dialogue: new Map(),
    activities: new Map(),
  });
  const [dialogueIncrements, setDialogueIncrements] = useState<Map<number, number>>(new Map());
  const [totalUnreadCount, setTotalUnreadCount] = useState<number>(0);

  const addUnread = useCallback((customerId: number, type: UpdateType) => {
    setUnread((prev) => ({
      ...prev,
      [type]: new Map(prev[type]).set(customerId, Date.now()),
    }));
    if (type === 'dialogue') {
      setDialogueIncrements((prev) => {
        const newMap = new Map(prev);
        newMap.set(customerId, (prev.get(customerId) || 0) + 1);
        return newMap;
      });
    }
  }, []);

  const markRead = useCallback((customerId: number) => {
    setUnread((prev) => {
      const dialogue = new Map(prev.dialogue);
      const activities = new Map(prev.activities);
      dialogue.delete(customerId);
      activities.delete(customerId);
      return { dialogue, activities };
    });
    setDialogueIncrements((prev) => {
      const newMap = new Map(prev);
      newMap.delete(customerId);
      return newMap;
    });
  }, []);

  const getIncrementalCount = useCallback(
    (customerId: number) => dialogueIncrements.get(customerId) || 0,
    [dialogueIncrements],
  );

  const resetIncrementalCount = useCallback((customerId: number) => {
    setDialogueIncrements((prev) => {
      const newMap = new Map(prev);
      newMap.delete(customerId);
      return newMap;
    });
  }, []);

  const hasUnread = useCallback(
    (customerId: number) => {
      return unread.dialogue.has(customerId) || unread.activities.has(customerId);
    },
    [unread],
  );

  const getLatestTimestamp = useCallback(
    (customerId: number) => {
      const dialogueTs = unread.dialogue.get(customerId) ?? 0;
      const activitiesTs = unread.activities.get(customerId) ?? 0;
      return Math.max(dialogueTs, activitiesTs);
    },
    [unread],
  );

  const sortWithUnreadFirst = useCallback(
    <T extends {id: number}>(items: T[]) => {
      return [...items].sort((a, b) => {
        const aTime = getLatestTimestamp(a.id);
        const bTime = getLatestTimestamp(b.id);
        if (aTime === 0 && bTime === 0) return 0;
        if (aTime === 0) return 1;
        if (bTime === 0) return -1;
        return bTime - aTime;
      });
    },
    [getLatestTimestamp],
  );

  const contextValue = useMemo(
    () => ({
      unread,
      dialogueIncrements,
      totalUnreadCount,
      addUnread,
      markRead,
      hasUnread,
      sortWithUnreadFirst,
      getIncrementalCount,
      resetIncrementalCount,
      setTotalUnreadCount,
    }),
    [unread, dialogueIncrements, totalUnreadCount, addUnread, markRead, hasUnread, sortWithUnreadFirst, getIncrementalCount, resetIncrementalCount],
  );

  return <UnifiedDialogContext.Provider value={contextValue}>{children}</UnifiedDialogContext.Provider>;
};

export default UnifiedDialogContextProvider;
